import { Badge, Box, Container, Group, Image, Menu, Text, UnstyledButton } from '@mantine/core';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAttentionCounts } from '@/api/ops';
import { useAuth } from '@/auth/useAuth';
import { GlobalSearch } from '@/features/ops/GlobalSearch';
import { brand } from '@/theme/theme';

const NAV_LINKS = [
  { to: '/staff/ops', labelKey: 'nav.stalls' },
  { to: '/staff/projects', labelKey: 'nav.projects' },
  { to: '/staff/runs', labelKey: 'nav.runs' },
  { to: '/staff/missions', labelKey: 'nav.missions' },
  { to: '/staff/organizations', labelKey: 'nav.organizations' },
] as const;

const MORE_LINKS = [
  { to: '/staff/run-types', labelKey: 'nav.runTypes' },
  { to: '/staff/analysis-types', labelKey: 'nav.analysisTypes' },
  { to: '/staff/search', labelKey: 'nav.search' },
  { to: '/staff/audit', labelKey: 'nav.audit' },
  { to: '/staff/slack', labelKey: 'nav.slack' },
] as const;

/** Staff-console page shell: white top bar with nav, a link back to the client face. See frontend-design.md §5.2. */
export function StaffShell({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const moreActive = MORE_LINKS.some((l) => pathname.startsWith(l.to));
  const attention = useAttentionCounts();
  // The stuck board's open rows, and runs that failed in the last day or are stalled now.
  const badges: Partial<Record<(typeof NAV_LINKS)[number]['to'], { n: number; title?: string }>> = {
    '/staff/ops': { n: attention.data?.stalls ?? 0 },
    '/staff/runs': { n: attention.data?.runs ?? 0, title: t('nav.runsAttention') },
  };

  const handleSignOut = async () => {
    await signOut();
    await navigate({ to: '/login' });
  };

  return (
    <Box bg={brand.bg} mih="100vh">
      <Box bg={brand.surface} style={{ borderBottom: `1px solid ${brand.border}` }}>
        <Container size="xl" px={24}>
          {/* One row at 1280 px too: the nav counts made it wrap, so the search gives way instead. */}
          <Group justify="space-between" h={64} wrap="nowrap">
            <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
              <UnstyledButton component={Link} to="/">
                <Image src="/logo-green.png" alt={t('app.name')} h={28} w="auto" fit="contain" />
              </UnstyledButton>
              <Box w={1} h={24} bg={brand.border} />
              <Text fz={12} fw={700} tt="uppercase" c={brand.text} bg={brand.accentBg} px={10} py={4} style={{ borderRadius: 999 }}>
                {t('app.staff')}
              </Text>
              <Group gap="xs" ml="sm" wrap="nowrap">
                {NAV_LINKS.map((link) => (
                  <UnstyledButton
                    key={link.to}
                    component={Link}
                    to={link.to}
                    px={10}
                    py={6}
                    style={{ borderRadius: 999 }}
                    activeProps={{ style: { borderRadius: 999, backgroundColor: brand.surface2 } }}
                  >
                    <Group gap={6} wrap="nowrap">
                      <Text fz={14} fw={600} c={brand.text}>
                        {t(link.labelKey)}
                      </Text>
                      {!!badges[link.to]?.n && (
                        <Badge size="sm" circle={badges[link.to]!.n < 10} color={link.to === '/staff/runs' ? 'red' : 'forest'} title={badges[link.to]?.title}>
                          {badges[link.to]!.n}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                ))}
                <Menu shadow="md" width={200} position="bottom-start">
                  <Menu.Target>
                    <UnstyledButton
                      px={10}
                      py={6}
                      data-active={moreActive ? 'true' : undefined}
                      style={{ borderRadius: 999, backgroundColor: moreActive ? brand.surface2 : undefined }}
                    >
                      <Text fz={14} fw={600} c={brand.text}>
                        {t('nav.more')} ▾
                      </Text>
                    </UnstyledButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {MORE_LINKS.map((link) => (
                      <Menu.Item key={link.to} component={Link} to={link.to}>
                        {t(link.labelKey)}
                      </Menu.Item>
                    ))}
                    <Menu.Divider />
                    {/* Django serves the admin; a full page load, not a router link. */}
                    <Menu.Item component="a" href="/admin/" target="_blank" rel="noopener">
                      {t('nav.djangoAdmin')} ↗
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
            <Group gap="sm" wrap="nowrap" miw={0}>
              <GlobalSearch />
              <UnstyledButton component={Link} to="/">
                <Text fz={13} c={brand.textMuted} style={{ whiteSpace: 'nowrap' }}>
                  {t('staff.backToClient')}
                </Text>
              </UnstyledButton>
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton px={10} py={6} maw={180} style={{ borderRadius: 999, border: `1px solid ${brand.border}` }}>
                    <Text fz={14} fw={600} c={brand.text} truncate="end" title={me?.full_name || me?.email || undefined}>
                      {me?.full_name || me?.email}
                    </Text>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item component={Link} to="/assistant-access">
                    {t('nav.assistantAccess')}
                  </Menu.Item>
                  <Menu.Item onClick={() => void handleSignOut()}>{t('nav.signOut')}</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Container>
      </Box>
      <Container size="xl" px={24} py={24}>
        {children}
      </Container>
    </Box>
  );
}
